/** Sessions page — guardian's upcoming sessions. */

import React from 'react';
import { parseISO } from 'date-fns';
import { useAuth } from '@core/auth/AuthContext';
import { usePortalSessions, type PortalSession } from '../api/portal';

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  padding: '0.875rem 1.25rem',
  marginBottom: '0.5rem',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

/** Format a UTC ISO timestamp in the tenant's local timezone.
 *
 * Per the project invariant, all timestamps are stored UTC and the frontend
 * MUST convert to the tenant timezone before display — a guardian travelling
 * abroad should still see session times in the school's local clock.
 */
function formatInTenantTz(iso: string, tenantTimezone: string): string {
  const date = parseISO(iso);
  try {
    return new Intl.DateTimeFormat('en-GB', {
      weekday:    'short',
      day:        'numeric',
      month:      'short',
      year:       'numeric',
      hour:       '2-digit',
      minute:     '2-digit',
      hour12:     false,
      timeZone:   tenantTimezone,
    }).format(date);
  } catch {
    // Fallback if the tenant_timezone is malformed — show UTC explicitly.
    return date.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
  }
}

const SessionRow: React.FC<{ session: PortalSession; tenantTimezone: string }> = ({
  session,
  tenantTimezone,
}) => {
  return (
    <div style={card} data-testid={`candeza-portal-session-row-${session.id}`}>
      <div>
        <strong>{session.class_name || 'Session'}</strong>
        <div style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: 2 }}>
          {formatInTenantTz(session.starts_at, tenantTimezone)}
        </div>
      </div>
      <span style={{
        fontSize: '0.75rem',
        background: '#e0e7ff',
        color: '#4f46e5',
        padding: '2px 8px',
        borderRadius: 12,
        textTransform: 'capitalize',
      }}>
        {session.status}
      </span>
    </div>
  );
};

const SessionsPage: React.FC = () => {
  const { data, isLoading, isError } = usePortalSessions();
  const { user } = useAuth();
  const sessions = data?.items ?? [];
  // Default to Asia/Kolkata if user not yet loaded — matches our only-tenant default
  // and avoids flashing browser-local times on the first paint.
  const tenantTimezone = user?.tenant_timezone ?? 'Asia/Kolkata';

  if (isLoading) {
    return <div data-testid="candeza-portal-sessions-loading">Loading sessions…</div>;
  }
  if (isError) {
    return (
      <div data-testid="candeza-portal-sessions-error" style={{ color: '#ef4444' }}>
        Could not load sessions.
      </div>
    );
  }

  return (
    <div data-testid="candeza-portal-sessions-page">
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.25rem' }}>
        Upcoming Sessions
      </h1>
      {sessions.length === 0 ? (
        <p data-testid="candeza-portal-sessions-empty" style={{ color: '#6b7280' }}>
          No upcoming sessions found.
        </p>
      ) : (
        <div data-testid="candeza-portal-sessions-list">
          {sessions.map(s => (
            <SessionRow key={s.id} session={s} tenantTimezone={tenantTimezone} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionsPage;

